import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, Divider } from "@material-ui/core";
import { Add, DeleteOutline, EditOutlined } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { listTemplates, createTemplate, updateTemplate, deleteTemplate, getTemplate } from "../../services/checklistTemplates";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

// Estilos inspirados no TicketDetailsModal
const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: '780px',
    width: '92%',
    borderRadius: 18,
    overflow: 'hidden',
    background: (theme.palette.mode === 'light' || theme.palette.type === 'light')
      ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
      : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  },
  dialogTitle: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1.5, 2),
    '& .MuiTypography-h6': {
      fontWeight: 600,
      fontSize: '1.05rem'
    }
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1),
    maxHeight: '70vh',
    overflowY: 'auto',
    '&::-webkit-scrollbar': { width: 8 },
    '&::-webkit-scrollbar-track': {
      background: (theme.palette.mode === 'light' || theme.palette.type === 'light') ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
      borderRadius: 4
    },
    '&::-webkit-scrollbar-thumb': {
      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
      borderRadius: 4
    }
  },
  styledTextField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      background: (theme.palette.mode === 'light' || theme.palette.type === 'light')
        ? 'rgba(255,255,255,0.95)'
        : 'rgba(255,255,255,0.06)',
      boxShadow: (theme.palette.mode === 'light' || theme.palette.type === 'light')
        ? '0 1px 2px rgba(0,0,0,0.04)'
        : '0 1px 2px rgba(0,0,0,0.35)',
      '& fieldset': { borderColor: theme.palette.divider, borderWidth: 1 },
      '&:hover fieldset': { borderColor: theme.palette.primary.light },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 3px ${theme.palette.primary.main}22` },
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: 10,
      paddingBottom: 10,
      fontSize: '0.85rem'
    },
    '& .MuiInputLabel-outlined': { transform: 'translate(14px, 14px) scale(1)' },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' }
  },
  stepsPanel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 16,
    padding: theme.spacing(1.5, 1.5, 1),
    background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? 'rgba(0,0,0,0.25)' : '#FFFFFF',
    position: 'relative'
  },
  stepsLegend: {
    position: 'absolute',
    top: -10,
    left: 18,
    padding: '0 6px',
    fontSize: '0.70rem',
    background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? 'rgba(0,0,0,0.25)' : '#FFFFFF',
    color: theme.palette.text.secondary,
    letterSpacing: 0.5,
    textTransform: 'uppercase'
  },
  stepCard: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    padding: theme.spacing(1.25, 1.25, 1),
    background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? 'rgba(255,255,255,0.05)' : '#FFFFFF',
    boxShadow: (theme.palette.type === 'dark' || theme.palette.mode === 'dark')
      ? '0 1px 2px rgba(0,0,0,0.55)'
      : '0 1px 2px rgba(0,0,0,0.08)',
    transition: 'background .2s, box-shadow .2s',
    marginBottom: theme.spacing(1.25),
    '&:hover': {
      background: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? 'rgba(255,255,255,0.08)' : 'rgba(250,250,250,0.7)'
    }
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  },
  stepIndex: {
    width: 26,
    height: 26,
    borderRadius: 8,
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '0.75rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  stepActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center'
  },
  inlineSmall: { maxWidth: 120 },
  footerActions: { padding: theme.spacing(1, 2) },
  tableWrapper: { overflowX: 'auto' }
}));

const StepRow = ({ step, onChange, onRemove }) => {
  const classes = useStyles();
  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={6}>
        <TextField fullWidth variant="outlined" size="small" label="Título do passo" value={step.title} onChange={(e)=>onChange({ ...step, title: e.target.value })} />
      </Grid>
      <Grid item xs={3}>
        <TextField fullWidth variant="outlined" size="small" type="number" label="Prazo" value={step.dueValue} onChange={(e)=>onChange({ ...step, dueValue: Number(e.target.value) })} />
      </Grid>
      <Grid item xs={2}>
        <TextField select fullWidth variant="outlined" size="small" label="Unidade" value={step.dueUnit} onChange={(e)=>onChange({ ...step, dueUnit: e.target.value })}>
          <MenuItem value="min">min</MenuItem>
          <MenuItem value="h">h</MenuItem>
          <MenuItem value="d">dia</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={1}>
        <IconButton onClick={onRemove}><DeleteOutline /></IconButton>
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth variant="outlined" size="small" multiline rows={2} label="Descrição do passo (opcional)" value={step.description || ""} onChange={(e)=>onChange({ ...step, description: e.target.value })} />
      </Grid>
    </Grid>
  );
};

const TemplateModal = ({ open, onClose, onSaved, templateId }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [responsibleUserId, setResponsibleUserId] = useState("");
  const [steps, setSteps] = useState([{ title: "", dueValue: 0, dueUnit: "h", description: null }]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  React.useEffect(()=>{
    if (open) {
      (async()=>{
        try {
          const { data } = await api.get("/users", { params: { pageNumber: 1 } });
          setUsers(data.users || []);
        } catch {}
      })();
    }
  }, [open]);

  React.useEffect(()=>{
    if (open && templateId) {
      (async()=>{
        try {
          const { data } = await getTemplate(templateId);
          setName(data.name || "");
          setResponsibleUserId(data.responsibleUserId || "");
          setSteps((data.steps || []).map(s => ({ title: s.title, dueValue: s.dueValue, dueUnit: s.dueUnit, description: s.description || "" })));
        } catch {}
      })();
    } else if (open && !templateId) {
      setName("");
      setResponsibleUserId("");
  setSteps([{ title: "", dueValue: 0, dueUnit: "h", description: null }]);
    }
  }, [open, templateId]);

  const updateStep = (idx, next) => {
    setSteps(prev => prev.map((s,i)=> i===idx ? next : s));
  };
  const removeStep = (idx) => {
    setSteps(prev => {
      const arr = prev.filter((_,i)=> i!==idx);
      return arr.length ? arr : [{ title: "", dueValue: 0, dueUnit: "h", description: "" }];
    });
  };

  const canSave = name.trim() && steps.every(s => s.title.trim());

  const handleSave = async () => {
    if (!canSave) { toast.error("Preencha nome e títulos dos passos"); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), responsibleUserId: responsibleUserId || null, steps: steps.map((s,idx)=>({ ...s, order: idx })) };
      if (templateId) await updateTemplate(templateId, payload); else await createTemplate(payload);
      toast.success("Modelo salvo");
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      toast.error("Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth classes={{ paper: classes.dialogPaper }}>
      <DialogTitle className={classes.dialogTitle}>{templateId ? "Editar modelo" : "Novo modelo"}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField autoFocus fullWidth label="Nome do modelo" variant="outlined" className={classes.styledTextField} value={name} onChange={(e)=>setName(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Responsável" variant="outlined" className={classes.styledTextField} value={responsibleUserId} onChange={(e)=>setResponsibleUserId(e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {users.map(u => (<MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.stepsPanel} elevation={0}>
              <span className={classes.stepsLegend}>Passos</span>
              <Box mt={1} />
              {steps.map((s, idx) => (
                <div key={idx} className={classes.stepCard}>
                  <div className={classes.stepHeader}>
                    <div className={classes.stepIndex}>#{idx+1}</div>
                    <TextField
                      variant="outlined"
                      label="Título"
                      className={classes.styledTextField}
                      value={s.title}
                      onChange={(e)=> updateStep(idx, { ...s, title: e.target.value })}
                      style={{ flex: 1, minWidth: 180 }}
                    />
                    <TextField
                      variant="outlined"
                      label="Prazo"
                      type="number"
                      className={classes.styledTextField}
                      value={s.dueValue}
                      onChange={(e)=> updateStep(idx, { ...s, dueValue: Number(e.target.value) })}
                      style={{ width: 100 }}
                    />
                    <TextField
                      select
                      variant="outlined"
                      label="Unidade"
                      className={classes.styledTextField}
                      value={s.dueUnit}
                      onChange={(e)=> updateStep(idx, { ...s, dueUnit: e.target.value })}
                      style={{ width: 110 }}
                    >
                      <MenuItem value="min">min</MenuItem>
                      <MenuItem value="h">hora</MenuItem>
                      <MenuItem value="d">dia</MenuItem>
                    </TextField>
                    <div className={classes.stepActions}>
                      <Tooltip title="Remover passo"><IconButton size="small" onClick={()=> removeStep(idx)}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                    </div>
                  </div>
                  <Box mt={1}>
                    {(!s.description) && (
                      <Typography variant="caption" style={{ cursor:'pointer', color:'#1976d2', fontWeight:500 }} onClick={()=> updateStep(idx, { ...s, description: "" })}>
                        + Adicionar descrição
                      </Typography>
                    )}
                    {(s.description !== undefined && s.description !== null) && (
                      <Box position="relative">
                        <TextField
                          variant="outlined"
                          label="Descrição do passo"
                          className={classes.styledTextField}
                          value={s.description}
                          onChange={(e)=> updateStep(idx, { ...s, description: e.target.value })}
                          onBlur={(e)=> { if (!e.target.value.trim()) updateStep(idx, { ...s, description: null }); }}
                          fullWidth
                          multiline
                          rows={4}
                          inputProps={{ style: { resize: 'vertical' } }}
                        />
                        <Tooltip title="Remover descrição">
                          <IconButton size="small" style={{ position:'absolute', top:4, right:4 }} onClick={()=> updateStep(idx, { ...s, description: null })}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </div>
              ))}
              <Box display='flex' justifyContent='flex-end' mt={1}>
                <Button startIcon={<Add />} onClick={()=> setSteps([...steps, { title: "", dueValue: 0, dueUnit: "h", description: null }])}>Adicionar passo</Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.footerActions}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={!canSave || saving}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

const ChecklistTemplatesPage = () => {
  const { user, socket } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchRows = async () => {
    try {
      const [{ data }, usersRes] = await Promise.all([
        listTemplates(),
        api.get("/users", { params: { pageNumber: 1 } })
      ]);
      const users = (usersRes && (usersRes.data?.users || [])) || [];
      const withNames = (Array.isArray(data) ? data : []).map(r => ({
        ...r,
        responsibleName: (users.find(u => u.id === r.responsibleUserId)?.name) || (r.responsibleUserId ? `Usuário ${r.responsibleUserId}` : "—"),
        stepsCount: r.stepsCount || (r.steps ? r.steps.length : undefined)
      }));
      setRows(withNames);
    } catch (err) { console.log(err); }
  };

  React.useEffect(()=>{ fetchRows(); }, []);

  React.useEffect(()=>{
    const ch = `company-${user.companyId}-checklist-template`;
    const refresh = ()=> fetchRows();
    socket.on(ch, refresh);
    return ()=> socket.off(ch, refresh);
  }, [socket, user.companyId]);

  const openNew = () => { setEditingId(null); setOpen(true); };
  const openEdit = (id) => { setEditingId(id); setOpen(true); };
  const onClose = () => { setOpen(false); setEditingId(null); };

  const onDelete = async (id) => {
    try { await deleteTemplate(id); toast.success("Excluído"); fetchRows(); } catch { toast.error("Erro ao excluir"); }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Modelos de Checklist</Typography>
        <Button color="primary" variant="contained" startIcon={<Add />} onClick={openNew}>Novo modelo</Button>
      </Box>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Passos</TableCell>
              <TableCell>Atualizado</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.name}</TableCell>
                <TableCell>{r.responsibleName}</TableCell>
                <TableCell>{typeof r.stepsCount === 'number' ? r.stepsCount : "—"}</TableCell>
                <TableCell>{new Date(r.updatedAt).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar"><IconButton onClick={()=>openEdit(r.id)}><EditOutlined /></IconButton></Tooltip>
                  <Tooltip title="Excluir"><IconButton onClick={()=>onDelete(r.id)}><DeleteOutline /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {open && (
        <TemplateModal open={open} onClose={onClose} onSaved={fetchRows} templateId={editingId} />
      )}
    </Box>
  );
};

export default ChecklistTemplatesPage;
